import { partition } from 'lodash';
import { useEffect, useState } from 'react';

import { getRecognizedToolsFromRun } from '@/Pages/Run/utils/getRecognizedToolsFromRun';
import { useChildRuns } from '@/Pages/Run/utils/useChildRuns';
import { useGroupChildByParentId } from '@/Pages/Run/utils/useGroupChildByParentId';
import { useDidUserRefocusPage } from '@/hooks/useDidUserUnfocusPage';
import { useSegmentAnalytics } from '@/hooks/useSegmentAnalytics';
import { useRunsQuery, useSessions } from '@/hooks/useSwr';
import {
  getMessageContentAsText,
  getMessageType,
  maybeGetArrayOfMessages,
  maybeGetOpenAIResponsesInputMessages,
} from '@/utils/messages';
import { lowerCaseIncludes } from '@/utils/stringUtils';

import {
  onboardingSystemMessagePrompt,
  onboardingToolName,
} from '../constants';

const useObservabilityOnboardingAnalytics = ({
  hasRun,
  hasModifiedSystemMessage,
  hasModifiedTools,
  projectName,
}: {
  hasRun: boolean;
  hasModifiedSystemMessage: boolean;
  hasModifiedTools: boolean;
  projectName: string;
}) => {
  const { analytics } = useSegmentAnalytics();
  const [trackedFirstRun, setTrackedFirstRun] = useState(false);
  const [trackedSystemMessageModified, setTrackedSystemMessageModified] =
    useState(false);
  const [trackedToolsModified, setTrackedToolsModified] = useState(false);

  useEffect(() => {
    if (hasRun && !trackedFirstRun) {
      analytics?.track('Observability Onboarding - First Run', {
        project_name: projectName,
      });
      setTrackedFirstRun(true);
    }
  }, [hasRun, trackedFirstRun, analytics, projectName]);
  useEffect(() => {
    if (hasModifiedSystemMessage && !trackedSystemMessageModified) {
      analytics?.track('Observability Onboarding - System Message Modified', {
        project_name: projectName,
      });
      setTrackedSystemMessageModified(true);
    }
  }, [
    hasModifiedSystemMessage,
    trackedSystemMessageModified,
    analytics,
    projectName,
  ]);
  useEffect(() => {
    if (hasModifiedTools && !trackedToolsModified) {
      analytics?.track('Observability Onboarding - Tools Modified', {
        project_name: projectName,
      });
      setTrackedToolsModified(true);
    }
  }, [hasModifiedTools, trackedToolsModified, analytics, projectName]);
};

export const useObservabilityOnboardingSessionAndRuns = ({
  projectName,
  lang,
}: {
  projectName: string;
  lang: 'python' | 'typescript';
}) => {
  const { didUserRefocusPageFrozen } = useDidUserRefocusPage(() => {
    if (!run) return;
    setIsLoadingSystemMessageQuery(!hasModifiedSystemMessage);
    setIsLoadingToolsQuery(!hasModifiedTools && hasModifiedSystemMessage);
  });
  const [isLoadingSystemMessageQuery, setIsLoadingSystemMessageQuery] =
    useState(false);
  const [isLoadingToolsQuery, setIsLoadingToolsQuery] = useState(false);

  const { data: sessions } = useSessions(
    {
      include_stats: false,
      name_contains: projectName,
      reference_free: true,
    },
    undefined,
    {
      // even though this is a streaming endpoint, new rows are not streamed in, so we need to poll
      refreshInterval: (data) => {
        return !data?.value?.rows?.length && didUserRefocusPageFrozen
          ? 1000
          : 0;
      },
      keepPreviousData: true,
    }
  );

  const hasSession = !!sessions?.total;

  const sessionId = sessions?.rows?.[0]?.id;

  const { data: runsQuery } = useRunsQuery(
    sessionId
      ? {
          session: [sessionId],
          limit: 1,
          skip_prev_cursor: true,
          is_root: true,
          filter: 'and(eq(is_root, true), eq(status, "success"))',
        }
      : null,
    undefined,
    {
      revalidateOnFocus: true,
      keepPreviousData: true,
      refreshInterval: () => {
        return didUserRefocusPageFrozen ? 1500 : 0;
      },
    }
  );
  const run = runsQuery?.runs[0];
  const childRuns = useChildRuns({
    sessionId: sessionId,
    traceId: run?.trace_id,
    run,
    rootRun: run,
    isEvaluator: false,
    shareToken: undefined,
    traceMaxStartTime: run?.start_time,
    traceMinStartTime: run?.start_time,
  });
  // Partition runs into root and child runs
  const [rootRuns, childRunsOnly] = partition(
    childRuns.data?.runs || [],
    (run) => run.trace_id === run.id
  );

  const childRunLookup = useGroupChildByParentId(
    childRunsOnly,
    childRuns.isLoading
  );

  const llmRun = childRunsOnly.find((r) => r.run_type === 'llm');

  const { data: llmRunQuery } = useRunsQuery(
    llmRun?.id && sessionId
      ? {
          id: [llmRun.id],
          session: [sessionId],
          limit: 1,
          skip_prev_cursor: true,
        }
      : null,
    undefined,
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const llmRunWithIO = llmRunQuery?.runs[0];
  const inputKeys = Object.keys(llmRunWithIO?.inputs || {});

  const isSingleInputKey = inputKeys.length === 1;
  const singleKey = isSingleInputKey ? inputKeys[0] : undefined;
  const maybeMessagesArray = singleKey
    ? maybeGetArrayOfMessages(llmRunWithIO?.inputs?.[singleKey])
    : inputKeys.find((k) => k === 'messages')
    ? maybeGetArrayOfMessages(llmRunWithIO?.inputs?.messages)
    : maybeGetArrayOfMessages(llmRunWithIO?.inputs);
  const openAIResponsesInput = maybeGetOpenAIResponsesInputMessages(
    llmRunWithIO?.inputs
  );

  const messages = maybeMessagesArray || openAIResponsesInput;

  const llmRunSystemMessage = messages?.find(
    (m) => getMessageType(m) === 'system'
  );
  const llmRunSystemMessageContent =
    getMessageContentAsText(llmRunSystemMessage);

  const hasModifiedSystemMessage = llmRunSystemMessageContent
    ? lowerCaseIncludes(
        llmRunSystemMessageContent,
        onboardingSystemMessagePrompt
      )
    : false;

  const recognizedFunctions = llmRunWithIO
    ? getRecognizedToolsFromRun(llmRunWithIO)
    : null;

  const hasModifiedTools = recognizedFunctions?.find(
    (f) => f.name === onboardingToolName(lang)
  );

  useObservabilityOnboardingAnalytics({
    hasRun: !!run,
    hasModifiedSystemMessage,
    hasModifiedTools,
    projectName,
  });

  return {
    hasSession,
    sessionId,
    run: rootRuns?.length ? rootRuns[0] : run,
    rootRuns: rootRuns.length ? rootRuns : run ? [run] : [],
    childRunLookup,
    hasModifiedSystemMessage,
    hasModifiedTools,
    isLoadingSystemMessageQuery,
    isLoadingToolsQuery,
  };
};
